import {
  ComponentProps,
  ComponentType,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';

import { RequirePermission } from '@gasbi/shared/common/providers/PermissionProvider';
import { DialogType } from '@gasbi/shared/web/components/Dialog';
import { DialogTitle } from '@gasbi/shared/web/components/Dialog/DialogTitle';
import { useFormik } from 'formik';
import { Schema } from 'yup';

import { UserPermissionType } from '@gasbi/shared/common/providers/PermissionProvider/types';
import { extractFormData } from '@/forms/utils/extractFormData';

import { ButtonProps } from '@mui/joy/Button';

import { ForwardedRefForm } from '@components/ActsAndDocumentsForm/components/ForwardedRefForm';
import { FormModalContext } from '@components/ActsAndDocumentsForm/hooks/useFormModalContext';
import FormDialog from '@components/FormDialog';
import { Button } from '@components/ui/skeletons';

type FormFieldComponent<P = object> = ComponentType<
  {
    control: ReturnType<typeof useFormik>;
  } & P
>;

type FormFieldProps<C extends FormFieldComponent> = Omit<
  ComponentProps<C>,
  'control'
>;

type ActFormModalProps<T, C extends FormFieldComponent> = {
  FormFields: C;
  buttonTitle?: string;
  dialogTitle?: ReactNode;
  buttonProps?: Partial<ButtonProps>;
  dialogSubtitle?: string;
  handleSubmit?: (formData: T) => void;
  initialValues?: T;
  validationSchema?: Schema | ((params: FormFieldProps<C>) => Schema);
  fieldsData?: any[];
  formFieldsProps?: FormFieldProps<C>;
  isModalOpen?: boolean;
  setIsModalOpen?: (isOpen: boolean) => void;
  onClose?: () => void;
  disableScroll?: boolean;
  submitButtonTitle?: string;
  icon?: ReactNode;
  hideCancelButton?: boolean;
  disabled?: boolean;
  permission?: UserPermissionType;
};

export const ActFormModal = <
  T extends Record<string, any>,
  C extends FormFieldComponent,
>({
  FormFields,
  buttonTitle,
  buttonProps = {},
  dialogTitle,
  dialogSubtitle,
  handleSubmit,
  initialValues = {} as T,
  validationSchema,
  fieldsData,
  formFieldsProps,
  isModalOpen: isModalOpenProp,
  setIsModalOpen: setIsModalOpenProp,
  onClose,
  disableScroll,
  submitButtonTitle,
  icon,
  hideCancelButton,
  disabled,
  permission,
}: ActFormModalProps<T, C>) => {
  const [isModalOpen, setIsModalOpenInternal] = useState<boolean>(false);
  const setIsModalOpen = setIsModalOpenProp ?? setIsModalOpenInternal;

  useEffect(() => {
    if (isModalOpenProp !== undefined) {
      setIsModalOpenInternal(isModalOpenProp);
    }
  }, [isModalOpenProp]);

  return (
    <>
      <RequirePermission name={permission} skip={!permission}>
        <Button
          onClick={() => setIsModalOpen(true)}
          size='sm'
          disabled={disabled}
          {...buttonProps}
        >
          {buttonTitle}
        </Button>
      </RequirePermission>
      <FormModalContext.Provider
        value={{
          fieldOptions: extractFormData(fieldsData),
        }}
      >
        <ActFormDialog
          FormFields={FormFields}
          dialogTitle={dialogTitle}
          dialogSubtitle={dialogSubtitle}
          handleSubmit={handleSubmit}
          initialValues={initialValues}
          validationSchema={validationSchema}
          isModalOpen={isModalOpen}
          setIsModalOpen={setIsModalOpen}
          formFieldsProps={formFieldsProps}
          onClose={onClose}
          disableScroll={disableScroll}
          submitButtonTitle={submitButtonTitle}
          icon={icon}
          hideCancelButton={hideCancelButton}
        />
      </FormModalContext.Provider>
    </>
  );
};

type ActFormDialog<T, C extends FormFieldComponent> = Pick<
  ActFormModalProps<T, C>,
  | 'dialogSubtitle'
  | 'dialogTitle'
  | 'validationSchema'
  | 'FormFields'
  | 'initialValues'
  | 'formFieldsProps'
> & {
  isModalOpen?: boolean;
  setIsModalOpen: (isOpen: boolean) => void;
  handleSubmit?: (formData: T) => void;
  onClose?: () => void;
  disableScroll?: boolean;
  hideCancelButton?: boolean;
  submitButtonTitle?: string;
  icon?: ReactNode;
};

export const ActFormDialog = <
  T extends Record<string, any>,
  C extends FormFieldComponent<any>,
>({
  FormFields,
  dialogTitle,
  dialogSubtitle,
  initialValues = {} as T,
  validationSchema,
  isModalOpen,
  setIsModalOpen,
  handleSubmit,
  formFieldsProps,
  onClose,
  disableScroll,
  hideCancelButton,
  submitButtonTitle,
  icon,
}: ActFormDialog<T, C>) => {
  const modalRef = useRef(null);

  return (
    <FormDialog
      width='660px'
      isModalOpen={isModalOpen}
      onDialogClose={() => {
        modalRef.current?.handleClose();
        onClose?.();
      }}
      disableScroll={disableScroll}
    >
      <DialogTitle
        subtitle={dialogSubtitle}
        title={dialogTitle}
        showSubtitle
        icon={icon}
        type={icon && DialogType.Preview}
      />
      <ForwardedRefForm
        ref={modalRef}
        onClose={() => setIsModalOpen(false)}
        initialValues={initialValues}
        handleSubmit={handleSubmit}
        validationSchema={validationSchema}
        schemaParams={formFieldsProps}
        hideCancelButton={hideCancelButton}
        submitButtonTitle={submitButtonTitle}
      >
        {control => {
          const props = { control, ...formFieldsProps } as ComponentProps<C>;
          return <FormFields {...props} />;
        }}
      </ForwardedRefForm>
    </FormDialog>
  );
};
